import {
  buildChallenge,
  challengeableConfounders,
} from "@bindery/shared/reader/gate";
import { foldQuote } from "@bindery/convex-retells/model/wordGloss";
import {
  cooldownSchedule,
  QUIZ_DRAWER_COOLDOWN,
} from "@bindery/ui/hooks/use-quiz-cooldown";
import {
  preferredPronunciation,
  type DictionaryEntry,
  type LookupResource,
  type ReferenceLookupPorts,
  type ReferenceLookupState,
  type ReferencePronunciation,
  type ReferenceSelection,
} from "./reference-lookup";

interface LookupSession {
  readonly selection: ReferenceSelection;
  readonly abort: AbortController;
  readonly forms: string[];
  dictionary: LookupResource<readonly DictionaryEntry[]>;
  pronunciations: LookupResource<readonly ReferencePronunciation[]>;
  autoplayPending: boolean;
}

export function createReferenceController(
  ports: ReferenceLookupPorts,
  initialAutoplay = true,
) {
  let state: ReferenceLookupState = {
    status: "closed",
    autoplay: initialAutoplay,
  };
  let session: LookupSession | null = null;
  let playback: AbortController | null = null;
  let timers: ReturnType<typeof setTimeout>[] = [];
  const listeners = new Set<() => void>();

  const publish = (next: ReferenceLookupState): void => {
    state = next;
    for (const listener of listeners) listener();
  };

  const clearTimers = (): void => {
    for (const timer of timers) clearTimeout(timer);
    timers = [];
  };

  const stopAudio = (): void => {
    playback?.abort();
    playback = null;
  };

  const isCurrent = (target: LookupSession): boolean =>
    session === target && !target.abort.signal.aborted;

  const play = async (
    target: LookupSession,
    pronunciation: ReferencePronunciation,
  ): Promise<void> => {
    if (
      !isCurrent(target) ||
      state.status !== "open" ||
      state.gate.status !== "open"
    )
      return;
    stopAudio();
    const request = new AbortController();
    playback = request;
    publish({ ...state, playback: "playing", playingId: pronunciation.id });
    try {
      await ports.play(pronunciation, request.signal);
      if (
        isCurrent(target) &&
        playback === request &&
        state.status === "open"
      ) {
        publish({ ...state, playback: "idle", playingId: null });
      }
    } catch {
      if (
        isCurrent(target) &&
        playback === request &&
        state.status === "open"
      ) {
        publish({ ...state, playback: "error", playingId: null });
      }
    } finally {
      if (playback === request) playback = null;
    }
  };

  const autoplay = (target: LookupSession): void => {
    if (
      !isCurrent(target) ||
      state.status !== "open" ||
      state.gate.status !== "open" ||
      !target.autoplayPending
    )
      return;
    const resource = target.pronunciations;
    if (resource.status === "loading" || resource.status === "idle") return;
    target.autoplayPending = false;
    if (!state.autoplay || resource.status !== "ready") return;
    const pronunciation = preferredPronunciation(resource.value);
    if (pronunciation) void play(target, pronunciation);
  };

  const reveal = (target: LookupSession): void => {
    if (
      !isCurrent(target) ||
      state.status !== "open" ||
      state.gate.status !== "open"
    )
      return;
    publish({
      ...state,
      answer:
        state.answer?.kind === "dictionary" || target.selection.context === null
          ? { kind: "dictionary", entries: target.dictionary }
          : { kind: "context", link: target.selection.context },
      pronunciations: target.pronunciations,
    });
    autoplay(target);
  };

  const loadDictionary = async (target: LookupSession): Promise<void> => {
    if (
      target.dictionary.status === "loading" ||
      target.dictionary.status === "ready"
    )
      return;
    target.dictionary = { status: "loading" };
    reveal(target);
    try {
      const entries = await ports.dictionary(
        target.selection,
        target.abort.signal,
      );
      if (!isCurrent(target)) return;
      target.dictionary =
        entries.length > 0
          ? { status: "ready", value: entries }
          : { status: "missing" };
    } catch {
      if (!isCurrent(target)) return;
      target.dictionary = { status: "error" };
    }
    reveal(target);
  };

  const loadPronunciations = async (target: LookupSession): Promise<void> => {
    if (
      target.pronunciations.status === "loading" ||
      target.pronunciations.status === "ready"
    )
      return;
    target.pronunciations = { status: "loading" };
    reveal(target);
    try {
      const pronunciations = await ports.pronunciations(
        target.selection,
        target.abort.signal,
      );
      if (!isCurrent(target)) return;
      target.pronunciations =
        pronunciations.length > 0
          ? { status: "ready", value: pronunciations }
          : { status: "missing" };
    } catch {
      if (!isCurrent(target)) return;
      target.pronunciations = { status: "error" };
    }
    reveal(target);
  };

  const close = (): void => {
    session?.abort.abort();
    session = null;
    stopAudio();
    clearTimers();
    publish({ status: "closed", autoplay: state.autoplay });
  };

  const open = (selection: ReferenceSelection): void => {
    close();
    const forms = selection.singleWord
      ? challengeableConfounders(
          selection.text,
          selection.confounders,
          foldQuote,
        )
      : [];
    const target: LookupSession = {
      selection,
      forms,
      abort: new AbortController(),
      dictionary: { status: "idle" },
      pronunciations: { status: "idle" },
      autoplayPending: state.autoplay,
    };
    session = target;
    publish({
      status: "open",
      autoplay: state.autoplay,
      selection,
      gate:
        forms.length === 0
          ? { status: "open" }
          : {
              status: "matching",
              choices: buildChallenge(selection.text, forms),
              selectedId: null,
              wrongCount: 0,
              phase: "idle",
            },
      answer: null,
      pronunciations: { status: "idle" },
      playback: "idle",
      playingId: null,
    });
    void loadPronunciations(target);
    if (selection.context === null) void loadDictionary(target);
    reveal(target);
  };

  const select = (id: number): void => {
    if (
      state.status !== "open" ||
      state.gate.status !== "matching" ||
      state.gate.phase !== "idle"
    )
      return;
    if (!state.gate.choices.some((choice) => choice.id === id)) return;
    publish({ ...state, gate: { ...state.gate, selectedId: id } });
  };

  const check = (): "correct" | "wrong" | null => {
    const target = session;
    if (
      !target ||
      state.status !== "open" ||
      state.gate.status !== "matching" ||
      state.gate.phase !== "idle"
    )
      return null;
    const gate = state.gate;
    const chosen = gate.choices.find((choice) => choice.id === gate.selectedId);
    if (!chosen) return null;
    if (chosen.correct) {
      clearTimers();
      publish({ ...state, gate: { status: "open" } });
      reveal(target);
      return "correct";
    }
    const wrongCount = state.gate.wrongCount + 1;
    publish({
      ...state,
      gate: { ...state.gate, wrongCount, phase: "shaking" },
    });
    timers = cooldownSchedule(QUIZ_DRAWER_COOLDOWN, wrongCount).map((step) =>
      setTimeout(() => {
        if (
          !isCurrent(target) ||
          state.status !== "open" ||
          state.gate.status !== "matching"
        )
          return;
        publish({
          ...state,
          gate: {
            ...state.gate,
            phase: step.phase,
            selectedId:
              step.reshuffle || step.phase === "idle"
                ? null
                : state.gate.selectedId,
            choices: step.reshuffle
              ? buildChallenge(target.selection.text, target.forms)
              : state.gate.choices,
          },
        });
      }, step.at),
    );
    return "wrong";
  };

  const showDictionary = (): void => {
    if (!session || state.status !== "open" || state.gate.status !== "open")
      return;
    publish({
      ...state,
      answer: { kind: "dictionary", entries: session.dictionary },
    });
    void loadDictionary(session);
  };

  const showContext = (): void => {
    if (
      !session?.selection.context ||
      state.status !== "open" ||
      state.gate.status !== "open"
    )
      return;
    publish({
      ...state,
      answer: { kind: "context", link: session.selection.context },
    });
  };

  const replay = (id?: string): void => {
    if (
      !session ||
      state.status !== "open" ||
      state.gate.status !== "open" ||
      session.pronunciations.status !== "ready"
    )
      return;
    const values = session.pronunciations.value;
    const pronunciation =
      id === undefined
        ? preferredPronunciation(values)
        : values.find((p) => p.id === id && p.audioUrl !== null);
    if (!pronunciation) return;
    session.autoplayPending = false;
    void play(session, pronunciation);
  };

  const setAutoplay = (enabled: boolean): void => {
    if (!enabled) {
      if (session) session.autoplayPending = false;
      stopAudio();
    }
    publish(
      state.status === "open" && !enabled
        ? { ...state, autoplay: enabled, playback: "idle", playingId: null }
        : { ...state, autoplay: enabled },
    );
  };

  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    open,
    close,
    select,
    check,
    showDictionary,
    showContext,
    replay,
    setAutoplay,
    retryPronunciations: () => {
      if (session && state.status === "open" && state.gate.status === "open")
        void loadPronunciations(session);
    },
  };
}
